import {
  CompetitionLevel,
  getInvitationsOrganizationsUnverifiedQueryKey,
  postOrganizationsByOrgIdEditMutation,
  postOrganizationsByOrgIdVerifyMutation,
  Roles,
  SportType,
  User,
} from "@aura-frontend/omnibus-az-client";
import { Organization } from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  toast,
} from "@win-reality/react-ui";
import { useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAppContext } from "@/hooks/use-app-context";

import {
  ReviewAccountVerificationForm,
  ReviewFormData,
} from "./ReviewAccountVerificationForm";

interface ReviewAccountVerificationProps {
  organization: Organization | undefined;
  owner: User | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export const ReviewAccountVerificationDialog = ({
  organization,
  owner,
  isOpen,
  onClose,
}: ReviewAccountVerificationProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const verifyAccount = useMutation(postOrganizationsByOrgIdVerifyMutation());
  const editAccount = useMutation(postOrganizationsByOrgIdEditMutation());
  const queryClient = useQueryClient();
  const { user } = useAppContext();

  const handleCancel = () => {
    onClose();
    setIsEditing(false);
  };

  if (!organization || !owner) {
    return null;
  }

  const handleAccept = async () => {
    try {
      await verifyAccount.mutateAsync({
        path: {
          org_id: encodeURIComponent(organization.orgId),
        },
      });

      queryClient.invalidateQueries({
        queryKey: getInvitationsOrganizationsUnverifiedQueryKey(),
      });
      onClose();
    } catch (error) {
      toast("Failed to verify account", { variant: "error" });
      setIsEditing(false);
    }
  };

  const handleSubmit = async (data: ReviewFormData) => {
    try {
      await editAccount.mutateAsync({
        path: {
          org_id: encodeURIComponent(organization.orgId),
        },
        body: {
          name: data.accountName,
          sport: data.sport as SportType,
          competitionLevel: data.competitionLevel,
          city: data.city,
          state: data.state,
        },
      });
    } catch (error) {
      toast("Failed to edit account", { variant: "error" });
    }
    await handleAccept();
    setIsEditing(false);
  };

  const isSuperAdmin =
    user?.roles.includes("magnus_super_admin" as Roles) ?? false;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleCancel()}
      defaultCancelButton={false}
      size="xl"
    >
      {({ setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <div className="flex justify-between items-center w-full">
                <DialogTitle>Account Verification</DialogTitle>
                <TooltipProvider>
                  <Tooltip
                    delayDuration={0}
                    open={!isSuperAdmin ? undefined : false}
                  >
                    <TooltipTrigger asChild className="cursor-default">
                      <div>
                        <Button
                          variant="secondary-flat-light"
                          type="button"
                          onClick={() => setIsEditing(true)}
                          isDisabled={!isSuperAdmin}
                        >
                          Edit account info
                        </Button>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Only super admins can edit account info.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </DialogHeader>

            <ReviewAccountVerificationForm
              isEditing={isEditing}
              onSubmit={handleSubmit}
              onAccept={handleAccept}
              onCancel={handleCancel}
              reviewFormData={{
                accountName: organization.orgName,
                sport: (organization.sport ?? "") as SportType,
                competitionLevel: (organization.competitionLevel ??
                  "") as CompetitionLevel,
                city: organization.city ?? "",
                state: organization.state ?? "",
                firstName: owner.givenName ?? "",
                lastName: owner.familyName ?? "",
                accountOwner: owner.emails?.[0] ?? "",
              }}
              isLoading={verifyAccount.isPending || editAccount.isPending}
              isSuperAdmin={isSuperAdmin}
            />
          </DialogScrollableContent>
        </>
      )}
    </Dialog>
  );
};
